import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectSelectedItem,
  selectProjectUsers,
  selectProjectId,
  selectUserRole,
  selectItemsForParent,
} from '@/store/selectors';
import { setSelectedItem } from '../../app/features/project/projectSlice';
import { setItemsForParent } from '@/store/itemsSlice';
import { getProjectFilesNormalized } from '@/api/project';
import { Modal } from '@/components/ui/Modal';
import type { ProjectTask } from '@/types/task';
import FileUploadingModal from '../../components/modals/fileUploadingModal/FileUploadingModal';
import DeleteItemModal from '../../components/modals/deleteItemModal/DeleteItemModal';
import { WorkSection } from './WorkSection';
import { ProjectChat } from './ProjectChat';
import type { NormalizedItem } from '@/components/itemList/types';

/** Retrieve the last-viewed preview item ID for a project from localStorage. */
function getStoredPreviewItemId(projectId: string): string | undefined {
  try {
    const list = JSON.parse(localStorage.getItem('previewItemOnShowInProjectsList') || 'null');
    return list?.find((i: { project: string }) => i.project === projectId)?.previewItemOnShowId;
  } catch {
    return undefined;
  }
}

interface ProjectMainViewProps {
  currentUserGroup: string | null;
  /** Override the project ID (e.g. for guest pages where the URL param is a hash, not a project ID). */
  projectId?: string;
}

export function ProjectMainView({ currentUserGroup, projectId: projectIdProp }: ProjectMainViewProps) {
  const { id: paramId } = useParams<{ id: string }>();
  const projectId = projectIdProp || paramId;
  const dispatch = useAppDispatch();

  const selectedItem = useAppSelector(selectSelectedItem) as NormalizedItem | null;
  const users = useAppSelector(selectProjectUsers) as unknown[] | null;
  const reduxProjectId = useAppSelector(selectProjectId) as string | null;
  const userRole = useAppSelector(selectUserRole);
  const items = useAppSelector(selectItemsForParent(projectId)) as NormalizedItem[] | undefined;

  const [messagesTabOpen, setMessagesTabOpen] = useState(true);
  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
  const [modalOpen, setModalOpen] = useState<string | false>(false);
  const [modalParams, setModalParams] = useState<Record<string, unknown> | undefined>(undefined);

  // Construct legacy match object for sub-components that still need it
  const match = {
    params: { id: projectId! },
    url: `/in/project/${projectId}`,
  };

  // Auto-select a preview item when items load but nothing is selected
  useEffect(() => {
    if (!selectedItem && items?.length && projectId) {
      const prevId = getStoredPreviewItemId(projectId);
      const previewItems = items.filter((i) => i.preview);
      if (previewItems.length) {
        dispatch(setSelectedItem(previewItems.find((i) => i.id === prevId) || previewItems[0]));
      }
    }
  }, [selectedItem, items, projectId, dispatch]);

  // Clear selected task when switching to messages tab
  useEffect(() => {
    if (messagesTabOpen) setSelectedTask(undefined);
  }, [messagesTabOpen]);

  const updateModalOpen = useCallback((type?: string, params?: Record<string, unknown>) => {
    if (type) {
      setModalParams(params);
      setModalOpen(type);
    } else {
      setModalOpen(false);
    }
  }, []);

  /** Re-fetch items after upload and sync to global cache */
  const refreshItems = useCallback(() => {
    if (!projectId) return;
    setTimeout(() => {
      getProjectFilesNormalized(projectId).then((normalized) => {
        dispatch(setItemsForParent({ parentId: projectId, items: normalized }));
      });
    }, 1000);
  }, [projectId, dispatch]);

  return (
    <>
      <h5 className="px-1 text-sm font-medium text-muted-foreground truncate">
        {selectedItem?.title}
      </h5>

      <div className="mt-4 flex w-full max-lg:flex-col">
        <WorkSection
          messagesTabOpen={messagesTabOpen}
          selectedTask={selectedTask}
          updateSelectedTask={setSelectedTask}
          updateModalOpen={updateModalOpen}
          match={match}
        />
        {!!users && (
          <div className="ml-auto w-[30%] flex rounded-lg max-lg:mx-auto max-lg:w-full max-lg:max-w-160">
            <ProjectChat
              messagesTabOpen={messagesTabOpen}
              updateMessagesTabOpen={setMessagesTabOpen}
              updateSelectedTask={setSelectedTask}
              updateModalOpen={updateModalOpen}
            />
          </div>
        )}
      </div>

      {/* File upload modal */}
      <Modal
        open={modalOpen === 'fileUpload'}
        onClose={() => updateModalOpen()}
        title="Upload files"
        size="lg"
      >
        <FileUploadingModal
          closeModal={updateModalOpen}
          onRefresh={refreshItems}
          parentId={reduxProjectId}
          existingNames={items?.map((i: NormalizedItem) => i.name || i.title) || []}
          useCase="project"
        />
      </Modal>

      {/* Delete item modal */}
      <Modal
        open={modalOpen === 'deleteItem'}
        onClose={() => updateModalOpen()}
        title="Delete selected item?"
        size="sm"
      >
        <DeleteItemModal
          closeModal={updateModalOpen}
          action={modalParams?.action}
          selectedItems={[]}
          userIsAdmin={false}
          updateIsQuerying={() => {}}
        />
      </Modal>
    </>
  );
}
